/**
 * The Notification Center's settings view (#1095), behind the gear in the
 * panel header: per category, whether its notifications show in the panel
 * (In-App) and raise a toast, plus clearing read notifications and the
 * retention note.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { CATEGORY_ORDER } from '@/lib/utils/notificationGrouping'
import { NOTIFICATION_CHANNELS, type NotificationChannel } from '@/lib/utils/notificationPreferences'
import type { UseNotificationPreferencesReturn } from '@/hooks/useNotificationPreferences'
import type { NotificationCategory } from '@/services/notification-service'
import { FOCUS_RING } from './notificationStyles'

interface NotificationSettingsProps {
  preferences: UseNotificationPreferencesReturn
  hasRead: boolean
  onDeleteRead: () => void
}

const GRID = 'grid grid-cols-[minmax(0,1fr)_56px_56px] gap-2 items-center'

export function NotificationSettings({ preferences, hasRead, onDeleteRead }: NotificationSettingsProps) {
  const { t } = useTranslation()
  const [saveFailed, setSaveFailed] = useState(false)
  const { preferences: current, canEdit, setPreference } = preferences

  const change = (category: NotificationCategory, channel: NotificationChannel, enabled: boolean) => {
    setSaveFailed(false)
    void setPreference(category, channel, enabled).then((saved) => {
      if (!saved) setSaveFailed(true)
    })
  }

  return (
    <div data-testid="notification-settings" className="flex-auto min-h-0 overflow-y-auto px-[18px] py-4 flex flex-col gap-5">
      <section aria-labelledby="notification-settings-categories" className="flex flex-col gap-1">
        <h3 id="notification-settings-categories" className="text-sm font-semibold text-[var(--text-color)]">
          {t('notificationCenter.settings.categoriesTitle')}
        </h3>
        <p className="text-xs text-[var(--text-secondary)]">{t('notificationCenter.settings.intro')}</p>

        <div
          aria-hidden
          className={`${GRID} pt-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--text-secondary)]`}
        >
          <span>{t('notificationCenter.settings.category')}</span>
          <span className="text-center">{t('notificationCenter.settings.channels.inApp')}</span>
          <span className="text-center">{t('notificationCenter.settings.channels.toast')}</span>
        </div>

        <ul>
          {CATEGORY_ORDER.map((category) => {
            const label = t(`notificationCenter.chips.${category}`)
            return (
              <li
                key={category}
                data-testid={`notification-preference-${category}`}
                className={`${GRID} py-2 border-t border-[var(--window-border-color)]`}
              >
                <span className="flex flex-col gap-px min-w-0">
                  <span className="text-[13px] font-medium text-[var(--text-color)]">{label}</span>
                  <span className="text-xs text-[var(--text-secondary)]">
                    {t(`notificationCenter.settings.hints.${category}`)}
                  </span>
                </span>
                {NOTIFICATION_CHANNELS.map((channel) => (
                  <span key={channel} className="flex justify-center" data-testid={`notification-preference-${category}-${channel}`}>
                    <ToggleSwitch
                      checked={current[category][channel]}
                      onChange={(enabled) => change(category, channel, enabled)}
                      disabled={!canEdit}
                      ariaLabel={t(`notificationCenter.settings.switchLabel.${channel}`, { category: label })}
                    />
                  </span>
                ))}
              </li>
            )
          })}
        </ul>

        {saveFailed && (
          <p role="alert" className="text-xs text-destructive pt-1">
            {t('notificationCenter.settings.saveError')}
          </p>
        )}
        {!canEdit && (
          <p className="text-xs text-[var(--text-secondary)] pt-1">{t('notificationCenter.settings.notLoaded')}</p>
        )}

        <dl className="pt-2 flex flex-col gap-1.5 text-xs text-[var(--text-secondary)]">
          <div>
            <dt className="inline font-semibold text-[var(--text-color)]">{t('notificationCenter.settings.channels.inApp')}: </dt>
            <dd className="inline">{t('notificationCenter.settings.explain.inApp')}</dd>
          </div>
          <div>
            <dt className="inline font-semibold text-[var(--text-color)]">{t('notificationCenter.settings.channels.toast')}: </dt>
            <dd className="inline">{t('notificationCenter.settings.explain.toast')}</dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby="notification-settings-cleanup" className="flex flex-col gap-2 border-t border-[var(--window-border-color)] pt-4">
        <h3 id="notification-settings-cleanup" className="text-sm font-semibold text-[var(--text-color)]">
          {t('notificationCenter.settings.cleanupTitle')}
        </h3>
        <p className="text-xs text-[var(--text-secondary)]">{t('notificationCenter.settings.retention')}</p>
        <div>
          <button
            type="button"
            data-testid="notification-delete-read"
            onClick={onDeleteRead}
            disabled={!hasRead}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[var(--window-border-color)] text-xs font-medium text-[var(--text-color)] hover:bg-[var(--menu-item-hover)] disabled:opacity-40 disabled:pointer-events-none ${FOCUS_RING}`}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            {t('notificationCenter.deleteRead')}
          </button>
        </div>
      </section>
    </div>
  )
}
